import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, InputNumber, Space, Typography, Button } from 'antd';
import './StockComponents.css';

const { Text } = Typography;

const StockChart = ({ data, stockInfo, onSimulatedData }) => {
  const [hoverIndex, setHoverIndex] = useState(-1);
  const chartRef = useRef(null);
  const dataZoomRef = useRef(null);
  const [rsvN, setRsvN] = useState(9);
  const [kdjM1, setKdjM1] = useState(3);
  const [kdjM2, setKdjM2] = useState(3);
  const [lastClosePrice, setLastClosePrice] = useState(null);

  useEffect(() => {
    if (data && data.length > 0) {
      setLastClosePrice(data[data.length - 1].close);
    }
  }, [data]);

  /**
   * 模拟计算数据
   */
  const handleSimulate = () => {
    if (!data || data.length === 0 || !lastClosePrice) {
      return;
    }

    const simulatedData = [...data];
    const n = rsvN;
    const m1 = kdjM1;
    const m2 = kdjM2;

    let kValues = [];
    let dValues = [];
    let jValues = [];

    for (let i = 0; i < simulatedData.length; i++) {
      if (i < n - 1) {
        kValues.push('-');
        dValues.push('-');
        jValues.push('-');
      } else {
        let high = -Infinity;
        let low = Infinity;
        for (let j = i - n + 1; j <= i; j++) {
          high = Math.max(high, simulatedData[j].high);
          low = Math.min(low, simulatedData[j].low);
        }
        
        const close = simulatedData[i].close;
        const rsv = (close - low) / (high - low) * 100;

        let k, d, j;
        if (i === n - 1) {
          k = rsv;
          d = k;
        } else {
          k = (1 / m1) * rsv + (1 - 1 / m1) * parseFloat(kValues[i - 1] || 0);
          d = (1 / m2) * k + (1 - 1 / m2) * parseFloat(dValues[i - 1] || 0);
        }
        j = 3 * k - 2 * d;

        kValues.push(k);
        dValues.push(d);
        jValues.push(j);
      }
    }

    const lastData = simulatedData[simulatedData.length - 1];
    const simulatedDay = {
      trade_date: '模拟',
      open: lastClosePrice,
      close: lastClosePrice,
      high: Math.max(lastClosePrice, lastData.high),
      low: Math.min(lastClosePrice, lastData.low),
      vol: lastData.vol,
      amount: lastData.amount
    };

    simulatedData.push(simulatedDay);

    if (onSimulatedData) {
      onSimulatedData({
        data: simulatedData,
        parameters: { n, m1, m2, lastClosePrice }
      });
    }
  };

  /**
   * 计算移动平均线
   */
  const calculateMA = (data, period) => {
    if (!data || data.length === 0) return [];
    
    const result = [];
    for (let i = 0; i < data.length; i++) {
      if (i < period - 1) {
        result.push('-');
        continue;
      }
      let sum = 0;
      for (let j = 0; j < period; j++) {
        sum += data[i - j].close;
      }
      result.push((sum / period).toFixed(2));
    }
    return result;
  };

  /**
   * 计算RSV指标
   */
  const calculateRSV = (data, n = 9) => {
    if (!data || data.length === 0) {
      return [];
    }
    
    const result = [];

    for (let i = 0; i < data.length; i++) {
      if (i < n - 1) {
        result.push('-');
      } else {
        let high = -Infinity;
        let low = Infinity;
        for (let j = i - n + 1; j <= i; j++) {
          high = Math.max(high, data[j].high);
          low = Math.min(low, data[j].low);
        }
        
        const close = data[i].close;
        const rsv = (close - low) / (high - low) * 100;
        result.push(rsv.toFixed(2));
      }
    }

    return result;
  };

  /**
   * 计算KDJ指标
   */
  const calculateKDJ = (data, n = 9, m1 = 3, m2 = 3) => {
    if (!data || data.length === 0) {
      return { k: [], d: [], j: [] };
    }
    
    const kValues = [];
    const dValues = [];
    const jValues = [];

    for (let i = 0; i < data.length; i++) {
      if (i < n - 1) {
        kValues.push('-');
        dValues.push('-');
        jValues.push('-');
      } else {
        let high = -Infinity;
        let low = Infinity;
        for (let j = i - n + 1; j <= i; j++) {
          high = Math.max(high, data[j].high);
          low = Math.min(low, data[j].low);
        }
        
        const close = data[i].close;
        const rsv = (close - low) / (high - low) * 100;

        let k, d, j;
        if (i === n - 1) {
          k = rsv;
          d = k;
        } else {
          k = (1 / m1) * rsv + (1 - 1 / m1) * parseFloat(kValues[i - 1] || 0);
          d = (1 / m2) * k + (1 - 1 / m2) * parseFloat(dValues[i - 1] || 0);
        }
        j = 3 * k - 2 * d;

        kValues.push(k.toFixed(3));
        dValues.push(d.toFixed(3));
        jValues.push(j.toFixed(3));
      }
    }

    return { k: kValues, d: dValues, j: jValues };
  };

  // 使用 useMemo 缓存计算结果
  const indicators = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        ma5: [],
        ma10: [],
        ma20: [],
        ma60: [],
        rsv: [],
        kdj: { k: [], d: [], j: [] }
      };
    }

    const ma5 = calculateMA(data, 5);
    const ma10 = calculateMA(data, 10);
    const ma20 = calculateMA(data, 20);
    const ma60 = calculateMA(data, 60);
    const rsv = calculateRSV(data, rsvN);
    const kdj = calculateKDJ(data, rsvN, kdjM1, kdjM2);

    return { ma5, ma10, ma20, ma60, rsv, kdj };
  }, [data, rsvN, kdjM1, kdjM2]);

  const getOption = () => {
    if (!data || data.length === 0) {
      return {
        title: {
          text: stockInfo ? `${stockInfo.name} (${stockInfo.ts_code})` : '暂无数据',
          left: 'center',
          textStyle: {
            fontSize: 18,
            fontWeight: 'bold'
          }
        },
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text: '暂无数据',
            fill: '#999',
            fontSize: 16
          }
        },
        // 完全禁用所有交互
        tooltip: {
          show: false
        },
        brush: {
          toolbox: []
        }
      };
    }

    const dates = data.map(item => item.trade_date);
    const values = data.map(item => [
      item.open,
      item.close,
      item.low,
      item.high
    ]);
    const volumes = data.map((item, index) => [index, item.vol, item.close > item.open ? 1 : -1]);

    const series = [
      {
        name: 'K线',
        type: 'candlestick',
        data: values,
        // 禁用该系列的鼠标事件
        silent: true,
        itemStyle: {
          color: '#ef5350',
          color0: '#26a69a',
          borderColor: '#ef5350',
          borderColor0: '#26a69a'
        }
      },
      {
        name: 'MA5',
        type: 'line',
        data: indicators.ma5,
        silent: true,
        smooth: true,
        lineStyle: {
          width: 1,
          color: '#ff9800'
        },
        itemStyle: {
          opacity: 0
        },
        symbol: 'none'
      },
      {
        name: 'MA10',
        type: 'line',
        data: indicators.ma10,
        silent: true,
        smooth: true,
        lineStyle: {
          width: 1,
          color: '#4caf50'
        },
        itemStyle: {
          opacity: 0
        },
        symbol: 'none'
      },
      {
        name: 'MA20',
        type: 'line',
        data: indicators.ma20,
        silent: true,
        smooth: true,
        lineStyle: {
          width: 1,
          color: '#2196f3'
        },
        itemStyle: {
          opacity: 0
        },
        symbol: 'none'
      },
      {
        name: 'MA60',
        type: 'line',
        data: indicators.ma60,
        silent: true,
        smooth: true,
        lineStyle: {
          width: 1,
          color: '#9c27b0'
        },
        itemStyle: {
          opacity: 0
        },
        symbol: 'none'
      },
      {
        name: '成交量',
        type: 'bar',
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: volumes,
        silent: true,
        itemStyle: {
          color: function (params) {
            return params.data[2] === 1 ? '#ef5350' : '#26a69a';
          }
        }
      },
      {
        name: 'RSV',
        type: 'line',
        xAxisIndex: 2,
        yAxisIndex: 2,
        data: indicators.rsv,
        silent: true,
        smooth: true,
        lineStyle: {
          width: 1,
          color: '#ff5722'
        },
        symbol: 'circle',
        symbolSize: 4,
        showSymbol: false
      },
      {
        name: 'K',
        type: 'line',
        xAxisIndex: 2,
        yAxisIndex: 2,
        data: indicators.kdj.k,
        silent: true,
        smooth: true,
        lineStyle: {
          width: 1,
          color: '#ff9800'
        },
        symbol: 'circle',
        symbolSize: 4,
        showSymbol: false
      },
      {
        name: 'D',
        type: 'line',
        xAxisIndex: 2,
        yAxisIndex: 2,
        data: indicators.kdj.d,
        silent: true,
        smooth: true,
        lineStyle: {
          width: 1,
          color: '#2196f3'
        },
        symbol: 'circle',
        symbolSize: 4,
        showSymbol: false
      },
      {
        name: 'J',
        type: 'line',
        xAxisIndex: 2,
        yAxisIndex: 2,
        data: indicators.kdj.j,
        silent: true,
        smooth: true,
        lineStyle: {
          width: 1,
          color: '#4caf50'
        },
        symbol: 'circle',
        symbolSize: 4,
        showSymbol: false
      }
    ];

    return {
      title: {
        text: stockInfo ? `${stockInfo.name} (${stockInfo.ts_code})` : '股票K线图',
        left: 'center',
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold'
        }
      },
      // 完全禁用 tooltip
      tooltip: {
        show: false,
        trigger: 'none'
      },
      // 禁用所有鼠标事件
      brush: {
        toolbox: []
      },
      // 禁用选择功能
      selectedMode: false,
      // 禁用图例选择
      legend: {
        data: ['K线', 'MA5', 'MA10', 'MA20', 'MA60', '成交量', 'RSV', 'K', 'D', 'J'],
        top: 30,
        selectedMode: false
      },
      grid: [
        {
          left: '10%',
          right: '8%',
          height: '40%'
        },
        {
          left: '10%',
          right: '8%',
          top: '45%',
          height: '15%'
        },
        {
          left: '10%',
          right: '8%',
          top: '65%',
          height: '20%'
        }
      ],
      xAxis: [
        {
          type: 'category',
          data: dates,
          scale: true,
          boundaryGap: false,
          axisLine: { onZero: false },
          splitLine: { show: false },
          min: 'dataMin',
          max: 'dataMax',
          // 禁用轴上的鼠标事件
          triggerEvent: false
        },
        {
          type: 'category',
          gridIndex: 1,
          data: dates,
          scale: true,
          boundaryGap: false,
          axisLine: { onZero: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          min: 'dataMin',
          max: 'dataMax',
          triggerEvent: false
        },
        {
          type: 'category',
          gridIndex: 2,
          data: dates,
          scale: true,
          boundaryGap: false,
          axisLine: { onZero: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          min: 'dataMin',
          max: 'dataMax',
          triggerEvent: false
        }
      ],
      yAxis: [
        {
          scale: true,
          splitArea: {
            show: true
          },
          triggerEvent: false
        },
        {
          scale: true,
          gridIndex: 1,
          splitNumber: 2,
          axisLabel: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          triggerEvent: false
        },
        {
          scale: true,
          gridIndex: 2,
          splitNumber: 5,
          min: 0,
          max: 100,
          axisLabel: {
            formatter: '{value}'
          },
          triggerEvent: false
        }
      ],
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: [0, 1, 2],
          zoomLock: false,
          start: dataZoomRef.current?.start || 0,
          end: dataZoomRef.current?.end || 100
        },
        {
          show: true,
          xAxisIndex: [0, 1, 2],
          type: 'slider',
          top: '90%',
          zoomLock: false,
          start: dataZoomRef.current?.start || 0,
          end: dataZoomRef.current?.end || 100
        }
      ],
      series: series,
      // 禁用动画效果（可选）
      animation: false
    };
  };

  // 根据鼠标悬停位置获取当前指标值
  const getCurrentIndicators = () => {
    if (!data || data.length === 0) {
      return {
        rsv: '-',
        k: '-',
        d: '-',
        j: '-',
        date: '',
        open: '-',
        close: '-',
        high: '-',
        low: '-',
        vol: '-',
        amount: '-',
        ma5: '-',
        ma10: '-',
        ma20: '-',
        ma60: '-'
      };
    }

    const index = hoverIndex >= 0 && hoverIndex < data.length ? hoverIndex : data.length - 1;
    const item = data[index];
    
    return {
      rsv: indicators.rsv[index] && indicators.rsv[index] !== '-' ? indicators.rsv[index] : '-',
      k: indicators.kdj.k[index] && indicators.kdj.k[index] !== '-' ? indicators.kdj.k[index] : '-',
      d: indicators.kdj.d[index] && indicators.kdj.d[index] !== '-' ? indicators.kdj.d[index] : '-',
      j: indicators.kdj.j[index] && indicators.kdj.j[index] !== '-' ? indicators.kdj.j[index] : '-',
      date: item ? item.trade_date : '',
      open: item ? item.open.toFixed(2) : '-',
      close: item ? item.close.toFixed(2) : '-',
      high: item ? item.high.toFixed(2) : '-',
      low: item ? item.low.toFixed(2) : '-',
      vol: item ? (item.vol / 10000).toFixed(2) + '万' : '-',
      amount: item && item.amount ? (item.amount * 1000 / 100000000).toFixed(2) + '亿' : '-',
      ma5: indicators.ma5[index] && indicators.ma5[index] !== '-' ? indicators.ma5[index] : '-',
      ma10: indicators.ma10[index] && indicators.ma10[index] !== '-' ? indicators.ma10[index] : '-',
      ma20: indicators.ma20[index] && indicators.ma20[index] !== '-' ? indicators.ma20[index] : '-',
      ma60: indicators.ma60[index] && indicators.ma60[index] !== '-' ? indicators.ma60[index] : '-'
    };
  };

  // 获取当前指标值
  const currentIndicators = useMemo(() => getCurrentIndicators(), [indicators, hoverIndex]);

  // 切换周期时重置缩放
  useEffect(() => {
    dataZoomRef.current = null;
  }, [data]);

  // 设置自定义的鼠标移动监听
  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;

    setTimeout(() => {
      const chartInstance = chartRef.current.getEchartsInstance();
      if (!chartInstance) return;

      const domElement = chartInstance.getDom();

      const handleMouseMove = (event) => {
        try {
          const rect = domElement.getBoundingClientRect();
          const x = event.clientX - rect.left;
          const y = event.clientY - rect.top;

          const pointInGrid = chartInstance.convertFromPixel({ gridIndex: 0 }, [x, y]);
          
          if (pointInGrid && pointInGrid[0] !== undefined) {
            const dataIndex = Math.round(pointInGrid[0]);
            if (dataIndex >= 0 && dataIndex < data.length) {
              setHoverIndex(dataIndex);
            }
          }
        } catch (error) {
        }
      };

      const handleMouseLeave = () => {
        setHoverIndex(-1);
      };

      const handleDataZoom = (params) => {
        if (params.batch) {
          dataZoomRef.current = {
            start: params.batch[0].start,
            end: params.batch[0].end
          };
        } else {
          dataZoomRef.current = {
            start: params.start,
            end: params.end
          };
        }
      };

      domElement.addEventListener('mousemove', handleMouseMove);
      domElement.addEventListener('mouseleave', handleMouseLeave);
      chartInstance.on('dataZoom', handleDataZoom);

      return () => {
        domElement.removeEventListener('mousemove', handleMouseMove);
        domElement.removeEventListener('mouseleave', handleMouseLeave);
        chartInstance.off('dataZoom', handleDataZoom);
      };
    }, 100);
  }, [data, indicators]);

  // 如果没有数据，显示空状态
  if (!data || data.length === 0) {
    return (
      <Card 
        className="chart-card" 
        variant="outlined"
        style={{ marginTop: 24 }}
      >
        <div style={{ 
          height: '800px', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          backgroundColor: '#fafafa',
          color: '#999',
          fontSize: '16px'
        }}>
          暂无数据
        </div>
      </Card>
    );
  }

  return (
    <Card 
      className="chart-card" 
      bordered={false}
      style={{ marginTop: 24 }}
    >
      {/* 参数设置区域 */}
      <div style={{
        padding: '10px',
        backgroundColor: '#f0f5ff',
        borderBottom: '1px solid #d9d9d9',
        fontSize: '14px',
        marginBottom: '10px'
      }}>
        <Space>
          <Text strong>参数设置：</Text>
          <Space>
            <Text>RSV(N):</Text>
            <InputNumber
              min={1}
              max={100}
              value={rsvN}
              onChange={(value) => setRsvN(value || 9)}
              style={{ width: 80 }}
            />
          </Space>
          <Space>
            <Text>KDJ(M1):</Text>
            <InputNumber
              min={1}
              max={50}
              value={kdjM1}
              onChange={(value) => setKdjM1(value || 3)}
              style={{ width: 80 }}
            />
          </Space>
          <Space>
            <Text>KDJ(M2):</Text>
            <InputNumber
              min={1}
              max={50}
              value={kdjM2}
              onChange={(value) => setKdjM2(value || 3)}
              style={{ width: 80 }}
            />
          </Space>
          <Space>
            <Text>最近一日收盘价:</Text>
            <InputNumber
              min={0}
              precision={2}
              value={lastClosePrice}
              onChange={(value) => setLastClosePrice(value)}
              style={{ width: 100 }}
            />
          </Space>
          <Button 
            type="primary" 
            onClick={handleSimulate}
          >
            模拟计算
          </Button>
        </Space>
      </div>
      {/* 指标显示区域 */}
      <div style={{
        padding: '10px',
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e8e8e8',
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif'
      }}>
        {/* 第一行：日期、开盘价、收盘价、最高价、最低价、成交量、成交额 */}
        <div style={{ marginBottom: '8px' }}>
          {currentIndicators.date && <span style={{ marginRight: '20px', fontWeight: 'bold' }}>{currentIndicators.date}</span>}
          <span style={{ marginRight: '15px' }}>开盘: {currentIndicators.open}</span>
          <span style={{ marginRight: '15px' }}>收盘: {currentIndicators.close}</span>
          <span style={{ marginRight: '15px' }}>最高: {currentIndicators.high}</span>
          <span style={{ marginRight: '15px' }}>最低: {currentIndicators.low}</span>
          <span style={{ marginRight: '15px' }}>成交量: {currentIndicators.vol}</span>
          <span style={{ marginRight: '15px' }}>成交额: {currentIndicators.amount}</span>
        </div>
        {/* 第二行：MA指标、RSV指标、KDJ指标 */}
        <div>
          <span style={{ marginRight: '15px', color: '#ff9800' }}>MA5: {currentIndicators.ma5}</span>
          <span style={{ marginRight: '15px', color: '#4caf50' }}>MA10: {currentIndicators.ma10}</span>
          <span style={{ marginRight: '15px', color: '#2196f3' }}>MA20: {currentIndicators.ma20}</span>
          <span style={{ marginRight: '15px', color: '#9c27b0' }}>MA60: {currentIndicators.ma60}</span>
          <span style={{ marginLeft: '15px', color: '#ff5722' }}>RSV: {currentIndicators.rsv}</span>
          <span style={{ marginLeft: '15px', color: '#ff9800' }}>K: {currentIndicators.k}</span>
          <span style={{ marginLeft: '15px', color: '#2196f3' }}>D: {currentIndicators.d}</span>
          <span style={{ marginLeft: '15px', color: '#4caf50' }}>J: {currentIndicators.j}</span>
        </div>
      </div>
      <ReactECharts
        ref={chartRef}
        option={getOption()}
        style={{ height: '800px' }}
        notMerge={true}
        lazyUpdate={true}
        onEvents={{}}
      />
    </Card>
  );
};

export default StockChart;